import { type CollectionProductItem } from '@/common/types/collection.type';
import type {
  SortConditionType,
  SortDirectionType,
} from '@/common/types/product.type';
import { type GetVendorCollectionProductsResponeType } from '@/common/types/response.type';
import { useInfiniteQuery } from '@tanstack/react-query';

import Client from '@/common/utils/client.utility';
import { createQueryStringUtility } from '@/common/utils/create-query-string.utility';

interface VendorCollectionProductsParams {
  AssortmentId: number;
  vendorId: number;
  searchByProductName?: string;
  sortDirection?: SortDirectionType;
  sortCondition?: SortConditionType;
  categoryList?: string[];
  isDiscounted?: boolean;
  isAvailable?: boolean;
  pageIndex?: number;
  pageSize: number;
}

export type GetVendorCollectionProductsType = (
  params: VendorCollectionProductsParams
) => Promise<GetVendorCollectionProductsResponeType<CollectionProductItem>>;

export const getVendorCollectionProducts: GetVendorCollectionProductsType =
  async ({
    AssortmentId,
    vendorId,
    pageSize,
    pageIndex,
    sortDirection,
    sortCondition,
    categoryList,
    isAvailable,
    isDiscounted,
    searchByProductName,
  }) => {
    const client = new Client();
    return await client.get(
      `/v1/Assortment/Shopper/Products${createQueryStringUtility({ AssortmentId, vendorId, searchByProductName, pageIndex, pageSize, sortDirection, sortCondition, isDiscounted, isAvailable, categoryList })}`
    );
  };

export function useGetVendorCollectionProducts(
  params: VendorCollectionProductsParams
) {
  return useInfiniteQuery({
    queryKey: ['Vendor-Collection-Products', params],
    queryFn: async ({ pageParam = 0 }) =>
      await getVendorCollectionProducts({ ...params, pageIndex: pageParam }),
    getNextPageParam: (lastPage, allPages) => {
      const products = lastPage.data?.products;

      if (products?.length && products.length >= params.pageSize) {
        return allPages.length;
      }

      return undefined;
    },
    initialPageParam: 0,
    retry: 0,

    staleTime: 600000,
  });
}
